import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  squadColumns,
  squadDefaultSort,
  type SquadColumnKey,
  type SquadRow,
} from '../config/squadStatsTableConfig';
import { apiFetch } from '../lib/api';

type SortDirection = 'asc' | 'desc';

export function SquadStatsTable({ onSelectSquad }: { onSelectSquad?: (squad: string) => void }): JSX.Element {
  const infoContent: Partial<Record<SquadColumnKey, { title: string; description: ReactNode }>> = {
    med: {
      title: 'Медицина',
      description: 'Сумма мед. очков, заработанных бойцами отряда.',
    },
    ego: {
      title: 'ЧСВ',
      description: (
        <div className="space-y-2">
          <div className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs">
            CH = FR / ATT
          </div>
          <div>
            <strong>FR</strong> — Кол-во фрагов отряда;
            <br />
            <strong>ATT</strong> — посещаемость отряда.
          </div>
        </div>
      ),
    },
    efficiency: {
      title: 'КПД',
      description: (
        <div className="space-y-2">
          <div className="space-y-1 rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs">
            <div>KPD = CH + KK + MED - TKS</div>
            <div>TKS = (TK / FR) - (TK / ATT)</div>
            <div>MED = M / ATT</div>
          </div>
          <dl className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-1 text-xs">
            <dt>
              <strong>CH</strong>
            </dt>
            <dd>— ЧСВ отряда</dd>
            <dt>
              <strong>KK</strong>
            </dt>
            <dd>— КС КПД отряда</dd>
            <dt>
              <strong>TK</strong>
            </dt>
            <dd>— ТК отряда</dd>
            <dt>
              <strong>FR</strong>
            </dt>
            <dd>— Количество фрагов отряда</dd>
            <dt>
              <strong>M</strong>
            </dt>
            <dd>— Мед. очки отряда</dd>
            <dt>
              <strong>ATT</strong>
            </dt>
            <dd>— Посещаемость отряда</dd>
          </dl>
          <p className="text-xs">
            <strong>КПД = 0</strong>, если отряд посетил менее 40% игр ротации или рассчитанное КПД получилось
            отрицательным. Считается, что отряд посетил игру, если на игре присутствовало более 4 игроков.
          </p>
        </div>
      ),
    },
    ksEfficiency: {
      title: 'КС КПД',
      description: (
        <div className="space-y-2">
          <div className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs">
            KK = (SW / SKG) × (SW / GN)
          </div>
          <div>
            <strong>SW</strong> — Кол-во побед отряда в роли КСа;
            <br />
            <strong>SKG</strong> — Кол-во игр отряда в роли КСа;
            <br />
            <strong>GN</strong> — Кол-во посещённых игр на ротации отрядом.
          </div>
        </div>
      ),
    },
  };

  const { data: squadStatsResponse, isLoading } = useQuery({
    queryKey: ['squad-stats'],
    queryFn: () => apiFetch<{ items: SquadRow[] }>('/squad-stats/'),
  });
  const [sortKey, setSortKey] = useState<SquadColumnKey>(squadDefaultSort.key);
  const [sortDirection, setSortDirection] = useState<SortDirection>(squadDefaultSort.direction);
  const [activeInfoKey, setActiveInfoKey] = useState<SquadColumnKey | null>(null);
  const [filters, setFilters] = useState<Record<SquadColumnKey, string>>(() => {
    const initial = {} as Record<SquadColumnKey, string>;
    squadColumns.forEach((col) => {
      initial[col.key] = '';
    });
    return initial;
  });

  const handleSort = (key: SquadColumnKey) => {
    setSortKey(key);
    setSortDirection((prev) => (key === sortKey ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
  };

  const handleFilterChange = (key: SquadColumnKey, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const rows = squadStatsResponse?.items ?? [];

  const efficiencyRange = useMemo(() => {
    const values = rows.map((row) => row.efficiency);
    if (values.length === 0) {
      return { min: 0, max: 0 };
    }
    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }, [rows]);

  const kpiStyle = (value: number) => {
    const spread = Math.max(efficiencyRange.max - efficiencyRange.min, 0.001);
    const norm = Math.max(0, Math.min(1, (value - efficiencyRange.min) / spread));
    const startAlpha = norm * 0.25;
    const endAlpha = norm * 0.7;

    return {
      background: `linear-gradient(90deg, rgba(255, 222, 0, ${startAlpha.toFixed(3)}) 0%, rgba(255, 222, 0, ${endAlpha.toFixed(3)}) 100%)`,
      color: '#0f1115',
    } as const;
  };

  const filteredAndSorted = useMemo(() => {
    const filtered = rows.filter((row) =>
      squadColumns.every((col) => {
        const query = filters[col.key]?.trim();
        if (!query) return true;
        const value = row[col.key];
        return value
          .toString()
          .toLowerCase()
          .includes(query.toLowerCase());
      })
    );

    const sorted = [...filtered].sort((a, b) => {
      const colMeta = squadColumns.find((col) => col.key === sortKey);
      if (!colMeta) return 0;
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      if (colMeta.type === 'number') {
        const diff = Number(aVal) - Number(bVal);
        return sortDirection === 'asc' ? diff : -diff;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr === bStr) return 0;
      const result = aStr > bStr ? 1 : -1;
      return sortDirection === 'asc' ? result : -result;
    });

    return sorted;
  }, [filters, rows, sortDirection, sortKey]);

  const renderSortIndicator = (key: SquadColumnKey) => {
    if (sortKey !== key) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const renderChampionStars = (count?: number) => {
    if (!count) return null;
    return (
      <div className="flex items-center justify-center gap-1">
        {Array.from({ length: count }).map((_, index) => (
          <img
            key={index}
            src="/icons/star.svg"
            alt="Чемпион ротации"
            title="Чемпион ротации"
            className="h-3 w-3"
          />
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (!activeInfoKey) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-info-key]')) return;
      setActiveInfoKey(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [activeInfoKey]);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">Сводный рейтинг по отрядам</p>
        <h2 className="text-3xl font-bold">Статистика отрядов</h2>
      </div>
      {rows.length === 0 ? (
        <div className="card p-6 text-center text-sm text-muted-foreground">
          {isLoading ? 'Загрузка статистики отрядов…' : 'Нет данных по отрядам.'}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {squadColumns.map((col) => (
                    <th key={col.key} className="px-3 py-3 text-center align-middle font-semibold">
                      <div className="mx-auto flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort(col.key)}
                          className="flex items-center justify-center gap-2 rounded-md px-2 py-1 text-foreground transition hover:bg-muted/70"
                        >
                          <span>{col.label}</span>
                          <span className="text-muted-foreground">{renderSortIndicator(col.key)}</span>
                        </button>
                        {infoContent[col.key] ? (
                          <div className="relative" data-info-key={col.key}>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setActiveInfoKey((prev) => (prev === col.key ? null : col.key));
                              }}
                              className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-[10px] font-semibold text-muted-foreground transition hover:text-foreground"
                              aria-label={`Справка: ${col.label}`}
                              aria-expanded={activeInfoKey === col.key}
                            >
                              ?
                            </button>
                            {activeInfoKey === col.key ? (
                              <div className="absolute right-0 top-7 z-20 w-64 rounded-md border border-border bg-card p-3 text-left text-xs text-foreground shadow-lg">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-semibold text-foreground">
                                    {infoContent[col.key]?.title}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setActiveInfoKey(null);
                                    }}
                                    className="flex h-6 w-6 items-center justify-center rounded-full text-sm text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
                                    aria-label="Закрыть подсказку"
                                  >
                                    ✕
                                  </button>
                                </div>
                                <div className="mt-1 text-muted-foreground">{infoContent[col.key]?.description}</div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
                <tr>
                  {squadColumns.map((col) => (
                    <th key={`${col.key}-filter`} className="px-3 pb-3 text-center align-middle">
                      <input
                        type="text"
                        value={filters[col.key]}
                        onChange={(e) => handleFilterChange(col.key, e.target.value)}
                        placeholder="Поиск"
                        className="w-full rounded-md border border-border bg-background px-2 py-1 text-center text-xs text-foreground focus:border-primary focus:outline-none"
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAndSorted.map((row, idx) => (
                  <motion.tr
                    key={row.name}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18, delay: idx * 0.02 }}
                    className={`${idx % 2 === 0 ? 'bg-card' : 'bg-card/60'} transition-colors hover:bg-muted/40`}
                  >
                    <td className="px-3 py-3 text-center align-middle font-semibold text-foreground dark:text-primary">
                      <div className="flex items-center justify-center gap-2">
                        <span>{row.rank}</span>
                        {row.delta ? (
                          <span
                            className={`text-xs font-semibold ${
                              row.delta > 0 ? 'text-emerald-600' : 'text-red-600'
                            }`}
                          >
                            {row.delta > 0 ? '↑' : '↓'} {Math.abs(row.delta)}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center align-middle font-semibold">
                      {(() => {
                        const hasBadge = Boolean(row.badge);
                        const hasStars = Boolean(row.championCount);
                        const hasDecorations = hasBadge || hasStars;
                        return (
                          <div
                            className={
                              hasDecorations
                                ? 'flex min-h-[52px] flex-col items-center justify-center gap-2'
                                : 'flex items-center justify-center'
                            }
                          >
                            {hasBadge ? (
                              <img
                                src={row.badge.icon}
                                alt={row.badge.title}
                                title={row.badge.title}
                                className="h-3 w-3"
                              />
                            ) : (
                              renderChampionStars(row.championCount)
                            )}
                        <a
                          href={`/squad-stats/${row.name.toLowerCase()}`}
                          className="hover:underline"
                          aria-label={`Открыть статистику отряда ${row.name}`}
                          onClick={(event) => {
                            event.preventDefault();
                            onSelectSquad?.(row.name);
                          }}
                        >
                          {row.name}
                        </a>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3 text-center align-middle">{row.frags}</td>
                    <td className="px-3 py-3 text-center align-middle">{row.tk}</td>
                    <td className="px-3 py-3 text-center align-middle">{row.survival}</td>
                    <td className="px-3 py-3 text-center align-middle">{row.attendance}</td>
                    <td className="px-3 py-3 text-center align-middle">{row.vehicleFrags}</td>
                    <td className="px-3 py-3 text-center align-middle">{row.infantryFrags}</td>
                    <td className="px-3 py-3 text-center align-middle">{row.med.toFixed(4)}</td>
                    <td className="px-3 py-3 text-center align-middle">{row.ksEfficiency.toFixed(3)}</td>
                    <td className="px-3 py-3 text-center align-middle">{row.ego.toFixed(4)}</td>
                    <td
                      className="px-3 py-3 text-center align-middle font-semibold rounded"
                      style={kpiStyle(row.efficiency)}
                    >
                      {row.efficiency.toFixed(3)}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
            По умолчанию отсортировано по КПД по убыванию. Используйте стрелки в заголовках и поисковые поля для фильтрации.
          </div>
        </div>
      )}
    </div>
  );
}
